import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Send, 
  Lock, 
  Unlock,
  Mic,
  MicOff,
  Users,
  MessageSquare,
  Trash2,
  AlertTriangle,
  User,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPage = () => {
  const { user, messages, setMessages, onlineUsers, sendWsMessage, isSecurityHead, hasPermission } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [encrypted, setEncrypted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [team, setTeam] = useState([]);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [activeTab, setActiveTab] = useState('group');
  const [selectedDmUser, setSelectedDmUser] = useState(null);
  const [dmMessages, setDmMessages] = useState([]);
  const [emergencyMessages, setEmergencyMessages] = useState([]);
  const [groupMessages, setGroupMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const recordingTimerRef = useRef(null);

  const canDeleteMessages = hasPermission('can_delete_messages') || hasPermission('is_admin') || isSecurityHead;
  const canUseChat = hasPermission('can_use_chat');
  const canUseVTT = hasPermission('can_use_ptt') || hasPermission('can_use_chat');
  const canClearChat = hasPermission('can_clear_chat') || isSecurityHead;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async (chatType = null, recipientId = null) => {
    try {
      let url = `${API_URL}/messages`;
      const params = new URLSearchParams();
      if (chatType) params.append('chat_type', chatType);
      if (recipientId) params.append('recipient_id', recipientId);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      return response.data.reverse();
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      return [];
    }
  };

  const loadChatMessages = async () => {
    if (activeTab === 'emergency') {
      const msgs = await fetchMessages('emergency');
      setEmergencyMessages(msgs);
    } else if (activeTab === 'group') {
      const msgs = await fetchMessages('group');
      setGroupMessages(msgs);
    } else if (activeTab === 'individual' && selectedDmUser) {
      const msgs = await fetchMessages(null, selectedDmUser.id);
      setDmMessages(msgs);
    }
  };

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API_URL}/team`);
      setTeam(response.data.filter(m => m.status === 'active'));
    } catch (error) {
      console.error('Failed to fetch team:', error);
    }
  };

  const clearChat = async () => {
    setClearing(true);
    try {
      const chatType = activeTab === 'individual' ? 'individual' : activeTab;
      await axios.delete(`${API_URL}/messages/clear/${chatType}`);
      
      if (activeTab === 'emergency') setEmergencyMessages([]);
      else if (activeTab === 'group') setGroupMessages([]);
      else if (activeTab === 'individual') setDmMessages([]);
      
      toast.success(`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} chat cleared`);
      setClearDialogOpen(false);
    } catch (error) {
      toast.error('Failed to clear chat');
    } finally {
      setClearing(false);
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API_URL}/messages/${messageId}`);
      
      // Remove from appropriate message list
      if (activeTab === 'emergency') {
        setEmergencyMessages(prev => prev.filter(m => m.id !== messageId));
      } else if (activeTab === 'group') {
        setGroupMessages(prev => prev.filter(m => m.id !== messageId));
      } else if (activeTab === 'individual') {
        setDmMessages(prev => prev.filter(m => m.id !== messageId));
      }
      
      toast.success('Message deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  useEffect(() => {
    fetchTeam();
    loadChatMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadChatMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedDmUser]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [emergencyMessages, groupMessages, dmMessages]);

  // Listen for new messages via WebSocket
  useEffect(() => {
    // Filter new messages to appropriate chat
    const newMsgs = messages.filter(m => {
      if (m.chat_type === 'emergency' && !m.recipient_id) {
        return !emergencyMessages.some(em => em.id === m.id);
      }
      if (m.chat_type === 'group' && !m.recipient_id) {
        return !groupMessages.some(gm => gm.id === m.id);
      }
      return false;
    });
    
    newMsgs.forEach(msg => {
      if (msg.chat_type === 'emergency') {
        setEmergencyMessages(prev => [...prev, msg]);
      } else if (msg.chat_type === 'group' || !msg.chat_type) {
        setGroupMessages(prev => [...prev, msg]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageContent = newMessage;
    setNewMessage('');

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const tempMessage = {
      id: tempId,
      content: messageContent,
      sender_id: user?.id,
      sender_name: user?.name || 'You',
      encrypted: encrypted,
      chat_type: activeTab === 'individual' ? 'individual' : activeTab,
      created_at: new Date().toISOString(),
      isTemp: true
    };
    
    // Add to appropriate message list
    if (activeTab === 'emergency') {
      setEmergencyMessages(prev => [...prev, tempMessage]);
    } else if (activeTab === 'group') {
      setGroupMessages(prev => [...prev, tempMessage]);
    } else if (activeTab === 'individual') {
      setDmMessages(prev => [...prev, tempMessage]);
    }

    try {
      const payload = {
        content: messageContent,
        encrypted: encrypted,
        chat_type: activeTab === 'individual' ? 'individual' : activeTab,
        recipient_id: activeTab === 'individual' ? selectedDmUser?.id : null
      };
      
      const response = await axios.post(`${API_URL}/messages`, payload);
      
      // Replace temp message with actual
      if (response.data) {
        const updateFn = prev => {
          const filtered = prev.filter(m => m.id !== tempId);
          const exists = filtered.some(m => m.id === response.data.id);
          return exists ? filtered : [...filtered, response.data];
        };
        
        if (activeTab === 'emergency') setEmergencyMessages(updateFn);
        else if (activeTab === 'group') setGroupMessages(updateFn);
        else if (activeTab === 'individual') setDmMessages(updateFn);
      }
    } catch (error) {
      // Remove temp message on error
      const removeFn = prev => prev.filter(m => m.id !== tempId);
      if (activeTab === 'emergency') setEmergencyMessages(removeFn);
      else if (activeTab === 'group') setGroupMessages(removeFn);
      else if (activeTab === 'individual') setDmMessages(removeFn);
      
      toast.error('Failed to send message');
      setNewMessage(messageContent);
    }
  };

  // Voice-to-text recording - matching Emergency Chat pattern
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1
        }
      });
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : 'audio/webm';
      
      const recorder = new MediaRecorder(stream, { 
        mimeType,
        audioBitsPerSecond: 128000
      });
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      
      recorder.onstop = async () => {
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        setRecordingDuration(0);
        
        const blob = new Blob(chunks, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        
        if (blob.size < 1000) {
          toast.error('Recording too short. Hold longer.');
          return;
        }
        
        setIsTranscribing(true);
        toast.info('Processing speech...', { duration: 10000, id: 'transcribing' });
        
        try {
          const formData = new FormData();
          formData.append('file', blob, 'recording.webm');
          
          const response = await axios.post(`${API_URL}/voice/transcribe`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000
          });
          
          toast.dismiss('transcribing');
          
          if (response.data.text && response.data.text.trim()) {
            setNewMessage(prev => prev + (prev ? ' ' : '') + response.data.text.trim());
            toast.success('Speech recognized!');
          } else {
            toast.error('No speech detected');
          }
        } catch (error) {
          toast.dismiss('transcribing');
          console.error('Transcription error:', error);
          toast.error('Transcription failed');
        } finally {
          setIsTranscribing(false);
        }
      };
      
      recorder.start(100);
      setMediaRecorder(recorder);
      setIsRecording(true);
      
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Microphone error:', error);
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getCurrentMessages = () => {
    if (activeTab === 'emergency') return emergencyMessages;
    if (activeTab === 'group') return groupMessages;
    if (activeTab === 'individual') return dmMessages;
    return [];
  };

  const renderMessages = () => {
    const currentMessages = getCurrentMessages();
    
    if (activeTab === 'individual' && !selectedDmUser) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <User className="w-16 h-16 text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-center">Select a team member to start a conversation</p>
          <div className="mt-4 space-y-2 w-full max-w-sm">
            {team.filter(m => m.id !== user?.id).map((member) => (
              <button
                key={member.id}
                onClick={() => setSelectedDmUser(member)}
                className="w-full flex items-center gap-3 p-3 bg-surface hover:bg-white/10 rounded-sm border border-white/10 transition-all"
              >
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-cyan-500" />
                  </div>
                  <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-panel ${onlineUsers?.includes(member.id) ? 'bg-green-500' : 'bg-gray-500'}`} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-inter text-sm text-white">{member.name}</p>
                  <p className="font-mono text-[10px] text-muted-foreground">{member.role?.replace(/_/g, ' ')}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      );
    }

    return (
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {currentMessages.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="font-mono text-sm text-muted-foreground">No messages yet</p>
            </div>
          ) : (
            currentMessages.map((msg) => {
              const isOwnMessage = msg.sender_id === user?.id;
              const msgTime = msg.created_at ? new Date(msg.created_at) : new Date();
              const timeStr = msgTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
              const dateStr = msgTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              
              return (
                <div key={msg.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] ${isOwnMessage ? 'bg-primary/20 border-primary/30' : 'bg-surface border-white/10'} border rounded-sm p-3`}>
                    <div className="flex items-center justify-between gap-4 mb-1">
                      <span className="font-mono text-xs text-primary font-bold">{msg.sender_name}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-muted-foreground">{timeStr} • {dateStr}</span>
                        {msg.encrypted && <Lock className="w-3 h-3 text-green-500" />}
                        {canDeleteMessages && (
                          <button
                            onClick={() => deleteMessage(msg.id)}
                            className="p-1 hover:bg-red-500/20 rounded-sm transition-colors"
                            title="Delete message"
                          >
                            <Trash2 className="w-3 h-3 text-red-400 hover:text-red-300" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="font-inter text-sm text-white leading-relaxed">{msg.content}</p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="chat-page">
      <PageHeader 
        title="Team Chat" 
        subtitle="Encrypted communications"
        icon={<MessageSquare className="w-8 h-8" />}
      />

      <Card className="bg-panel border-white/10 rounded-sm h-[calc(100vh-200px)] flex flex-col">
        <CardHeader className="border-b border-white/10 pb-3">
          <div className="flex items-center justify-between">
            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedDmUser(null); }} className="w-full">
              <TabsList className="bg-surface border border-white/10">
                <TabsTrigger value="emergency" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-500">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Emergency
                </TabsTrigger>
                <TabsTrigger value="group" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-500">
                  <Users className="w-4 h-4 mr-2" />
                  Group
                </TabsTrigger>
                <TabsTrigger value="individual" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-500">
                  <User className="w-4 h-4 mr-2" />
                  Individual
                </TabsTrigger>
              </TabsList>
            </Tabs>
            
            {canClearChat && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setClearDialogOpen(true)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 ml-2"
                data-testid="clear-chat-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
          
          {/* Show selected DM user */}
          {activeTab === 'individual' && selectedDmUser && (
            <div className="flex items-center gap-3 mt-3 p-2 bg-cyan-500/10 rounded-sm border border-cyan-500/30">
              <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <User className="w-4 h-4 text-cyan-500" />
              </div>
              <div className="flex-1">
                <p className="font-inter text-sm text-white font-bold">{selectedDmUser.name}</p>
                <p className="font-mono text-[10px] text-muted-foreground">{selectedDmUser.role?.replace(/_/g, ' ')}</p>
              </div>
              <button
                onClick={() => setSelectedDmUser(null)}
                className="p-1 hover:bg-white/10 rounded-sm"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          )}
        </CardHeader>

        {/* Messages Area */}
        {renderMessages()}

        {/* Message Input */}
        {!canUseChat ? (
          <div className="p-6 border-t border-white/10 text-center" data-testid="chat-no-permission">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/10 border border-red-500/30 rounded-sm">
              <Lock className="w-4 h-4 text-red-400" />
              <span className="font-mono text-xs text-red-300 uppercase">
                Your role cannot send messages
              </span>
            </div>
          </div>
        ) : (activeTab !== 'individual' || selectedDmUser) && (
          <div className="p-4 border-t border-white/10">
            <form onSubmit={sendMessage} className="flex gap-2 items-center">
              {/* Voice-to-Text Button - Single tap toggle, 25% bigger */}
              {canUseVTT && (
                <button
                  type="button"
                  onClick={(e) => { 
                    e.preventDefault(); 
                    e.stopPropagation(); 
                    if (isRecording) {
                      stopRecording();
                    } else {
                      startRecording();
                    }
                  }}
                  disabled={isTranscribing}
                  className={`h-[70px] w-[70px] rounded-full shrink-0 flex items-center justify-center transition-colors select-none ${
                    isRecording 
                      ? 'bg-red-600 text-white shadow-lg shadow-red-500/50' 
                      : isTranscribing
                        ? 'bg-blue-600/50 text-white cursor-wait'
                        : 'bg-green-600 text-white hover:bg-green-500 shadow-md shadow-green-500/30'
                  }`}
                  style={{ 
                    WebkitUserSelect: 'none', 
                    userSelect: 'none', 
                    WebkitTouchCallout: 'none',
                    touchAction: 'manipulation'
                  }}
                  title={isRecording ? "Tap to stop" : "Tap to speak"}
                  data-testid="vtt-button"
                >
                  {isRecording ? (
                    <MicOff className="w-8 h-8 animate-pulse" />
                  ) : (
                    <Mic className="w-8 h-8" />
                  )}
                </button>
              )}
              
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={isRecording ? "🎤 Listening... Tap mic to stop" : `Message ${activeTab === 'emergency' ? 'emergency chat' : activeTab === 'individual' ? selectedDmUser?.name : 'group chat'}...`}
                className="flex-1 bg-black/50 border-white/30 rounded-full font-inter text-sm h-12 px-4"
                disabled={isTranscribing}
              />
              
              <Button type="submit" disabled={!newMessage.trim() || isTranscribing} className="h-12 w-12 bg-primary rounded-full shrink-0" data-testid="chat-send-button">
                <Send className="w-5 h-5" />
              </Button>
            </form>
            
            {isRecording && (
              <div className="mt-2 text-center">
                <span className="font-mono text-sm text-red-500 animate-pulse">
                  🎤 Recording: {formatDuration(recordingDuration)} - Tap mic to stop
                </span>
              </div>
            )}
            
            {isTranscribing && (
              <div className="mt-2 text-center">
                <span className="font-mono text-sm text-blue-400 animate-pulse">
                  Transcribing audio...
                </span>
              </div>
            )}
            
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-2">
                <Switch id="encrypted" checked={encrypted} onCheckedChange={setEncrypted} />
                <Label htmlFor="encrypted" className="font-mono text-xs text-muted-foreground flex items-center gap-1">
                  {encrypted ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                  Encrypt
                </Label>
              </div>
              <Badge variant="outline" className="font-mono text-[10px]">
                <Users className="w-3 h-3 mr-1" />
                {onlineUsers?.length || 0} online
              </Badge>
            </div>
          </div>
        )}
      </Card>

      {/* Clear Chat Dialog */}
      <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <AlertDialogContent className="bg-panel border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo text-white">Clear {activeTab} Chat</AlertDialogTitle>
            <AlertDialogDescription className="font-inter text-muted-foreground">
              This will permanently delete all messages in the {activeTab} chat. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={clearChat}
              disabled={clearing}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              {clearing ? 'Clearing...' : 'Clear All'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ChatPage;
